import {
    setMaxPages,
    setTotalCount,
    toggleCheckedItem,
    setSelectedUser,
    setTabCounts,
    setEditedUser,
    toggleEditModal,
    openMessageModal
} from '../../store/usersSlice';
import ApiService from '../../components/api_services/apiService.jsx';
import StatusBadge from '../../components/components/status_badge/StatusBadge.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';
import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as PersonalCard } from '../../components/icons/personalcard.svg';
import { ReactComponent as Chart } from '../../components/icons/chart.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as Lock } from '../../components/icons/lock.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';
import {
    buildProfilesQuery,
    extractProfilesEntries,
    extractProfilesNumberOfPages,
    extractProfilesTotalElements,
    filterUsersForTab,
    normalizeProfilesToUsers,
    sortRowsLocally,
} from './profilesAdapter.js';

const DEFAULT_USERS_PAGE_SIZE = 25;
const MAX_BACKEND_PROFILE_PAGES_SCAN = 200;

const SEX_LABELS = {
    MALE: 'mężczyzna',
    FEMALE: 'kobieta',
    OTHER: 'inna',
    UNSPECIFIED: '-',
};

const normalizeText = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text.length > 0 ? text : fallback;
};

const mapSex = (sex, optionalSexName) => {
    if (sex === 'OTHER' && optionalSexName) return optionalSexName;
    return SEX_LABELS[sex] || '-';
};

const buildFullName = (profile = {}) => {
    const first = normalizeText(profile?.name, '').trim();
    const last = normalizeText(profile?.surname, '').trim();
    const fullName = [first, last].filter(Boolean).join(' ').trim();
    return fullName || '-';
};

const formatReportDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const mapProfileToReportUser = (profile, fallbackId) => {
    if (!profile) {
        return {
            profileUuid: fallbackId || null,
            nickname: '-',
            fullName: '-',
            gender: '-',
            email: '-',
            description: '',
            birthday: '',
            sex: '',
            optionalSexName: '',
        };
    }

    return {
        profileUuid: profile.uuid || fallbackId || null,
        nickname: normalizeText(profile.nick, '-'),
        fullName: buildFullName(profile),
        gender: mapSex(profile.sex, profile.optionalSexName),
        email: normalizeText(profile.email, '-'),
        description: normalizeText(profile.description, ''),
        birthday: normalizeText(profile.birthday, ''),
        sex: normalizeText(profile.sex, ''),
        optionalSexName: normalizeText(profile.optionalSexName, ''),
        profileImageHashedId: profile.profileImageHashedId || profile.profileImageHash || '',
        profileImageUrl: profile.profileImageUrl || profile.imageUrl || '',
    };
};

const fetchProfilesMap = async (ids, requestOptions = {}) => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    const results = await Promise.allSettled(
        uniqueIds.map(async (id) => {
            const profile = await ApiService.getProfileById(id, requestOptions);
            return { id, profile };
        })
    );

    const map = new Map();
    results.forEach((result) => {
        if (result.status === 'fulfilled') {
            map.set(result.value.id, result.value.profile);
        }
    });

    return map;
};

const fetchReportRows = async (requestOptions = {}) => {
    const reportsResponse = await ApiService.getUserReports(requestOptions);
    const reports = Array.isArray(reportsResponse)
        ? reportsResponse
        : Array.isArray(reportsResponse?.data)
            ? reportsResponse.data
            : [];

    const profileIds = reports.flatMap((report) => [report?.reportedUserId, report?.reportSenderId]);
    const profilesMap = await fetchProfilesMap(profileIds, requestOptions);

    return reports.map((report, index) => {
        const reportedUserId = report?.reportedUserId || null;
        const reportSenderId = report?.reportSenderId || null;
        const reportedProfile = profilesMap.get(reportedUserId);
        const senderProfile = profilesMap.get(reportSenderId);

        const reported = mapProfileToReportUser(reportedProfile, reportedUserId);
        const sender = mapProfileToReportUser(senderProfile, reportSenderId);
        const reporterName = sender.nickname !== '-'
            ? `${sender.nickname} (${sender.fullName})`
            : normalizeText(reportSenderId, '-');

        return {
            id: report?.reportId ?? `rep-${index + 1}`,
            reportId: report?.reportId ?? null,
            reportedUserId,
            reportSenderId,
            createdAt: normalizeText(report?.createdAt, ''),
            profileUuid: reported.profileUuid,
            nickname: reported.nickname,
            fullName: reported.fullName,
            gender: report?.sex ? mapSex(report?.sex, '') : reported.gender,
            email: normalizeText(report?.email, reported.email),
            phone: normalizeText(report?.phoneNumber, '-'),
            reportDate: formatReportDateTime(report?.createdAt),
            reporterName,
            reportReason: normalizeText(report?.reason, '-'),
            status: 'pending',
            createdPacks: '-',
            likedEvents: '-',
            description: reported.description,
            birthday: reported.birthday,
            sex: normalizeText(report?.sex, reported.sex),
            optionalSexName: reported.optionalSexName,
            role: 'reports',
        };
    });
};

const fetchMessageReportRows = async (requestOptions = {}, groupBy = 'REPORT') => {
    const params = groupBy === 'CHAT'
        ? { groupBy, nestedGroupBy: 'REPORT' }
        : { groupBy };
    const response = await ApiService.getMessageReports(params, requestOptions);
    const payload = response?.items ? response : (Array.isArray(response?.data) ? { items: response.data } : response);
    const reports = Array.isArray(payload?.items) ? payload.items : [];
    const sourceReports = reports.map((report) => {
        if (groupBy === 'MESSAGE') return report?.reports?.[0] || {};
        if (groupBy === 'CHAT') return report?.items?.[0] || {};
        return report;
    });
    const profileIds = sourceReports.flatMap((report) => [
        report?.reportSenderId,
        report?.reportedUserId,
        report?.message?.senderId,
        report?.messages?.[0]?.message?.senderId,
        report?.items?.[0]?.messages?.[0]?.message?.senderId,
    ]).filter((id) => id && id !== '-');
    const profilesMap = await fetchProfilesMap(profileIds, requestOptions);

    return reports.map((report, index) => {
        const sourceReport = groupBy === 'MESSAGE'
            ? report?.reports?.[0] || {}
            : groupBy === 'CHAT' ? report?.items?.[0] || {} : report;
        const message = groupBy === 'MESSAGE'
            ? report?.message || {}
            : groupBy === 'CHAT'
                ? sourceReport?.messages?.[0]?.message || {}
                : report?.message || report?.messages?.[0]?.message || {};
        const reportSenderId = sourceReport?.reportSenderId || report?.reportSenderId || '-';
        const senderProfile = mapProfileToReportUser(profilesMap.get(reportSenderId), reportSenderId);
        const senderFromReport = sourceReport?.reportSender || report?.reportSender || {};
        const sender = {
            ...senderProfile,
            ...senderFromReport,
            profileUuid: senderFromReport?.profileUuid || senderProfile.profileUuid,
            nickname: senderFromReport?.nickname || senderProfile.nickname,
            fullName: senderFromReport?.fullName
                || senderFromReport?.displayName
                || senderProfile.fullName,
            email: senderFromReport?.email || senderProfile.email,
            profileImageHashedId: senderFromReport?.profileImageHashedId || senderProfile.profileImageHashedId,
            profileImageUrl: senderFromReport?.profileImageUrl || senderProfile.profileImageUrl,
        };
        const senderNickname = normalizeText(sender?.nickname, '');
        const senderDisplayName = normalizeText(sender?.displayName || sender?.fullName, '');
        const senderName = senderNickname
            ? `${senderNickname} (${senderDisplayName || '-'})`
            : normalizeText(reportSenderId, '-');
        const reportedUserId = sourceReport?.reportedUserId
            || report?.reportedUserId
            || message?.senderId
            || '-';
        const reportedUserProfile = mapProfileToReportUser(profilesMap.get(reportedUserId), reportedUserId);
        const reportedUserFromReport = sourceReport?.reportedUser || report?.reportedUser || {};
        const reportedUser = {
            ...reportedUserProfile,
            ...reportedUserFromReport,
            profileUuid: reportedUserFromReport?.profileUuid || reportedUserProfile.profileUuid,
            nickname: reportedUserFromReport?.nickname || reportedUserProfile.nickname,
            fullName: reportedUserFromReport?.fullName
                || reportedUserFromReport?.displayName
                || reportedUserProfile.fullName,
            email: reportedUserFromReport?.email || reportedUserProfile.email,
            profileImageHashedId: reportedUserFromReport?.profileImageHashedId || reportedUserProfile.profileImageHashedId,
            profileImageUrl: reportedUserFromReport?.profileImageUrl || reportedUserProfile.profileImageUrl,
        };
        const messages = groupBy === 'MESSAGE'
            ? (message?.id ? [{ reportId: sourceReport?.reportId, messageId: report?.messageId || message.id, message, messageAvailable: message?.isAvailable !== false }] : [])
            : groupBy === 'CHAT'
                ? (report?.items || []).flatMap((item) => item?.messages || [])
                : report?.messages || (message?.id ? [{
            reportId: report?.reportId,
            messageId: report?.messageId || message.id,
            message,
            messageAvailable: message?.isAvailable !== false,
        }] : []);
        const reportGroupId = groupBy === 'REPORT'
            ? report?.reportGroupId || report?.groupKey || null
            : null;
        const reportIds = groupBy === 'MESSAGE'
            ? (report?.reports || []).map((item) => item.reportId).filter(Boolean)
            : groupBy === 'CHAT'
                ? (report?.items || []).flatMap((item) => item.reportIds || [])
                : report?.reportIds || (report?.reportId ? [report.reportId] : []);

        return {
            id: reportGroupId || report?.groupKey || report?.reportId || `message-rep-${index + 1}`,
            reportId: sourceReport?.reportId ?? null,
            reportGroupId,
            groupKey: report?.groupKey || null,
            groupType: groupBy,
            messageId: report?.messageId || message?.id || '-',
            bunchId: report?.bunchId ?? message?.bunchId ?? '-',
            bunch: report?.bunch || null,
            reportSenderId,
            reportSender: sender,
            reportedUserId,
            reportedUser,
            senderName,
            reportDate: formatReportDateTime(message?.createdAt),
            createdAt: report?.createdAt || sourceReport?.createdAt || message?.createdAt || '',
            firstReportedAt: report?.firstReportedAt || sourceReport?.firstReportedAt || message?.createdAt || '',
            lastReportedAt: report?.lastReportedAt || sourceReport?.lastReportedAt || message?.createdAt || '',
            reportReason: normalizeText(sourceReport?.reason || report?.reason, '-'),
            reason: normalizeText(sourceReport?.reason || report?.reason, '-'),
            status: sourceReport?.status || report?.status || 'OPEN',
            reportCount: report?.reportCount ?? reportIds.length ?? 1,
            messageCount: report?.messageCount ?? messages.length,
            uniqueReporterCount: report?.uniqueReporterCount ?? 1,
            reportIds,
            reports: report?.reports || [],
            items: report?.items || [],
            nestedGroupBy: report?.nestedGroupBy || null,
            messages,
            availableActions: report?.availableActions || { canResolve: Boolean(reportGroupId) },
            messageContent: normalizeText(message?.text || message?.messageContent, '-'),
            role: 'messageReports',
        };
    });
};

const filterReports = (reports, keyword, filters = {}) => {
    const lowerKeyword = normalizeText(keyword, '').toLowerCase();
    let filtered = [...reports];

    if (lowerKeyword) {
        filtered = filtered.filter((report) => (
            String(report.nickname || '').toLowerCase().includes(lowerKeyword) ||
            String(report.fullName || '').toLowerCase().includes(lowerKeyword) ||
            String(report.email || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportReason || '').toLowerCase().includes(lowerKeyword) ||
            String(report.messageContent || '').toLowerCase().includes(lowerKeyword) ||
            String(report.messageId || '').toLowerCase().includes(lowerKeyword) ||
            String(report.bunchId || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reporterName || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportedUserId || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportSenderId || '').toLowerCase().includes(lowerKeyword)
        ));
    }

    if (filters.gender) {
        filtered = filtered.filter((report) => report.gender === filters.gender);
    }
    if (filters.status) {
        filtered = filtered.filter((report) => report.status === filters.status);
    }

    return filtered;
};

const getModerationTargetId = (user) => user?.profileUuid || user?.reportedUserId || null;

const promptForReason = (defaultReason = '') => {
    const normalizedDefaultReason = defaultReason && defaultReason !== '-' ? defaultReason : '';
    const enteredReason = window.prompt('Podaj powód:', normalizedDefaultReason);
    if (enteredReason === null) return null;
    return enteredReason.trim();
};

const runReportModerationAction = async (user, actionLabel, requestFn) => {
    const targetId = getModerationTargetId(user);
    if (!targetId) {
        alert('Brak ID użytkownika do wykonania akcji.');
        return;
    }

    const reason = promptForReason(user?.reportReason);
    if (reason === null) {
        return;
    }

    if (!reason) {
        alert('Powód jest wymagany.');
        return;
    }

    try {
        const payload = {
            id: targetId,
            reason,
        };

        if (user?.reportId !== undefined && user?.reportId !== null && user?.reportId !== '') {
            payload.reportId = user.reportId;
        }

        await requestFn(payload);
        alert(`${actionLabel} zostało wysłane.`);
    } catch (error) {
        console.error(`Nie udało się wykonać akcji "${actionLabel}":`, error);
    }
};

const normalizePacksCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const normalizeCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const enrichUsersWithCreatedPacks = async (users, requestOptions = {}, onCellUpdate = null) => {
    const countById = new Map();

    return Promise.all(users.map(async (user) => {
        if (typeof user?.createdPacks === 'number') {
            return user;
        }

        const userId = user?.id || user?._rawProfileEntry?.userResponseFullDto?.id || user?._rawProfileEntry?.profileResponseDto?.uuid;
        if (!userId || String(userId).startsWith('missing-user-id-')) {
            const result = {
                ...user,
                createdPacks: 0,
            };
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'createdPacks', 0);
            }
            return result;
        }

        if (countById.has(userId)) {
            const count = countById.get(userId);
            const result = {
                ...user,
                createdPacks: count,
            };
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'createdPacks', count);
            }
            return result;
        }

        try {
            const stats = await ApiService.getProfileStats(userId, {
                signal: requestOptions?.signal,
            });
            const statsData = stats?.data ?? stats;
            const count = statsData?.foundedBunches !== undefined && statsData?.foundedBunches !== null
                ? normalizePacksCount(statsData.foundedBunches)
                : null;

            if (count !== null) {
                countById.set(userId, count);
                if (typeof onCellUpdate === 'function') {
                    onCellUpdate(userId, 'createdPacks', count);
                }
                return {
                    ...user,
                    createdPacks: count,
                };
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                throw error;
            }
            try {
                const bunches = await ApiService.getBunchesCreatedByUser(userId, {
                    signal: requestOptions?.signal,
                });
                const bunchesList = Array.isArray(bunches)
                    ? bunches
                    : Array.isArray(bunches?.data)
                        ? bunches.data
                        : [];
                const count = bunchesList.length;
                countById.set(userId, count);
                if (typeof onCellUpdate === 'function') {
                    onCellUpdate(userId, 'createdPacks', count);
                }
                return {
                    ...user,
                    createdPacks: count,
                };
            } catch (fallbackError) {
                if (fallbackError?.code === 'ERR_CANCELED') {
                    throw fallbackError;
                }
                console.error('Error fetching user created packs count:', userId, fallbackError);
            }
        }

        countById.set(userId, 0);
        if (typeof onCellUpdate === 'function') {
            onCellUpdate(userId, 'createdPacks', 0);
        }
        return {
            ...user,
            createdPacks: 0,
        };
    }));
};

export const enrichUsersWithLikedEvents = async (users, requestOptions = {}, onCellUpdate = null) => {
    const countById = new Map();

    return Promise.all(users.map(async (user) => {
        if (typeof user?.likedEvents === 'number') {
            return user;
        }

        const userId = user?.id || user?._rawProfileEntry?.userResponseFullDto?.id || user?._rawProfileEntry?.profileResponseDto?.uuid;
        if (!userId || String(userId).startsWith('missing-user-id-')) {
            const result = {
                ...user,
                likedEvents: 0,
            };
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'likedEvents', 0);
            }
            return result;
        }

        if (countById.has(userId)) {
            const count = countById.get(userId);
            const result = {
                ...user,
                likedEvents: count,
            };
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'likedEvents', count);
            }
            return result;
        }

        try {
            const countResponse = await ApiService.getFavoriteEventsCountByUserId(userId, {
                signal: requestOptions?.signal,
            });
            const rawCount = countResponse?.data ?? countResponse;
            const count = normalizeCount(rawCount);
            countById.set(userId, count);
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'likedEvents', count);
            }
            return {
                ...user,
                likedEvents: count,
            };
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                throw error;
            }
            countById.set(userId, 0);
            if (typeof onCellUpdate === 'function') {
                onCellUpdate(userId, 'likedEvents', 0);
            }
            return {
                ...user,
                likedEvents: 0,
            };
        }
    }));
};

export const enrichUsersWithStats = async (users, requestOptions = {}, onCellUpdate = null) => {
    const [withPacks, withLiked] = await Promise.all([
        enrichUsersWithCreatedPacks(users, requestOptions, onCellUpdate),
        enrichUsersWithLikedEvents(users, requestOptions, onCellUpdate),
    ]);

    return users.map((user, idx) => ({
        ...user,
        createdPacks: withPacks[idx]?.createdPacks ?? user.createdPacks,
        likedEvents: withLiked[idx]?.likedEvents ?? user.likedEvents,
    }));
};

export const fetchUsers = async (
    currentPage,
    sortBy,
    sortDirection,
    keyword,
    pageTab,
    filters,
    dispatch,
    setRawUsers,
    requestOptions = {},
    messageReportsGrouping = 'REPORT',
    pageSize = DEFAULT_USERS_PAGE_SIZE
) => {
    const effectivePageSize = pageSize || DEFAULT_USERS_PAGE_SIZE;

    if (pageTab === 'messageReports') {
        try {
            const rows = await fetchMessageReportRows(requestOptions, messageReportsGrouping);
            const filtered = filterReports(rows, keyword, filters || {});
            const sorted = sortRowsLocally(filtered, sortBy, sortDirection);
            dispatch(setTotalCount(sorted.length));
            dispatch(setMaxPages(Math.max(1, Math.ceil(sorted.length / effectivePageSize))));
            const start = (currentPage - 1) * effectivePageSize;
            setRawUsers(sorted.slice(start, start + effectivePageSize));
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') return;
            console.error('Error fetching message reports:', error);
            setRawUsers([]);
            dispatch(setTotalCount(0));
            dispatch(setMaxPages(1));
        }
        return;
    }

    if (pageTab === 'reports') {
        try {
            if (requestOptions?.signal?.aborted) {
                return;
            }

            const reportRows = await fetchReportRows(requestOptions);
            const filteredReports = filterReports(reportRows, keyword, filters || {});
            const sortedReports = sortRowsLocally(filteredReports, sortBy, sortDirection);

            dispatch(setTabCounts({
                usersCount: 0,
                adminsCount: 0,
                reportsCount: reportRows.length,
                blockedCount: 0,
            }));

            dispatch(setTotalCount(sortedReports.length));
            const totalPages = Math.max(1, Math.ceil(sortedReports.length / effectivePageSize));
            dispatch(setMaxPages(totalPages));

            const startIndex = (currentPage - 1) * effectivePageSize;
            const paginated = sortedReports.slice(startIndex, startIndex + effectivePageSize);
            setRawUsers(paginated);
            return;
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                return;
            }
            console.error('Error fetching reports from /api/user/report:', error);
            setRawUsers([]);
            dispatch(setTotalCount(0));
            dispatch(setMaxPages(Math.max(1, currentPage)));
            dispatch(setTabCounts({ usersCount: 0, adminsCount: 0, reportsCount: 0, blockedCount: 0 }));
            return;
        }
    }

    try {
        const targetStartIndex = (currentPage - 1) * effectivePageSize;
        const targetEndIndex = targetStartIndex + effectivePageSize;

        let backendPage = 1;
        let exhausted = false;
        let scannedPages = 0;
        let collectedFilteredUsers = [];
        let backendTotalElements = null;

        while (!exhausted && scannedPages < MAX_BACKEND_PROFILE_PAGES_SCAN) {
            const query = buildProfilesQuery({
                pageNumber: backendPage,
                pageSize: effectivePageSize,
                searchText: keyword || '',
                sortBy,
                sortDirection,
                tableType: 'users',
                pageTab,
            });

            if (requestOptions?.signal?.aborted) {
                return;
            }

            const response = await ApiService.getSystemAdminProfiles(query, requestOptions);
            const backendEntries = extractProfilesEntries(response);
            const backendNumberOfPages = extractProfilesNumberOfPages(response);
            const totalElementsFromResp = extractProfilesTotalElements(response);
            if (totalElementsFromResp !== null && backendTotalElements === null) {
                backendTotalElements = totalElementsFromResp;
            }

            const normalizedUsers = normalizeProfilesToUsers(backendEntries);
            const filteredUsers = filterUsersForTab(normalizedUsers, pageTab, filters || {});
            collectedFilteredUsers = [...collectedFilteredUsers, ...filteredUsers];

            exhausted = backendNumberOfPages
                ? backendPage >= backendNumberOfPages
                : backendEntries.length < effectivePageSize;
            backendPage += 1;
            scannedPages += 1;
        }

        const currentPageRows = collectedFilteredUsers.slice(targetStartIndex, targetEndIndex);
        const sortedCurrentPageRows = sortRowsLocally(currentPageRows, sortBy, sortDirection);

        dispatch(setTabCounts({
            usersCount: 0,
            adminsCount: 0,
            reportsCount: 0,
            blockedCount: 0,
        }));

        const hasScannedAllPages = exhausted;
        const exactTotalPages = Math.max(1, Math.ceil(collectedFilteredUsers.length / effectivePageSize));
        const resolvedTotalPages = hasScannedAllPages ? exactTotalPages : Math.max(exactTotalPages, exactTotalPages + 1);

        const resolvedTotalCount = backendTotalElements !== null
            ? backendTotalElements
            : collectedFilteredUsers.length;

        dispatch(setTotalCount(resolvedTotalCount));
        dispatch(setMaxPages(resolvedTotalPages));

        setRawUsers(sortedCurrentPageRows);

        const onCellUpdate = (userId, field, value) => {
            if (requestOptions?.signal?.aborted) return;
            if (typeof setRawUsers === 'function') {
                setRawUsers((prevUsers) => {
                    const current = Array.isArray(prevUsers) ? prevUsers : sortedCurrentPageRows;
                    return current.map((u) => {
                        const uid = u?.id || u?._rawProfileEntry?.userResponseFullDto?.id || u?._rawProfileEntry?.profileResponseDto?.uuid;
                        if (uid === userId) {
                            return { ...u, [field]: value };
                        }
                        return u;
                    });
                });
            }
        };

        const rowsWithStats = await enrichUsersWithStats(sortedCurrentPageRows, requestOptions, onCellUpdate);
        if (!requestOptions?.signal?.aborted && typeof setRawUsers === 'function') {
            setRawUsers(rowsWithStats);
        }
    } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
            return;
        }
        console.error('Error fetching users from /api/system_admin/profiles:', error);
        setRawUsers([]);
        dispatch(setTotalCount(0));
        dispatch(setMaxPages(Math.max(1, currentPage)));
        dispatch(setTabCounts({ usersCount: 0, adminsCount: 0, reportsCount: 0, blockedCount: 0 }));
    }
};

export const mapUsersToRows = (users, checkedItems, dispatch, pageTab, options = {}) => {
    const { onReportDetails, onMessageReportDetails } = options;

    return users.map((user) => {
        const isChecked = checkedItems.includes(user.id);
        const statusLabel = user.status === 'blocked'
            ? 'Zablokowany'
            : user.status === 'pending'
                ? 'Nieaktywowany'
                : user.status === 'forDeleting'
                    ? 'Do usunięcia'
                    : 'Aktywny';
        const statusType = user.status === 'blocked'
            ? 'inactive'
            : user.status === 'pending' || user.status === 'forDeleting'
                ? 'pending'
                : 'active';

        const defaultActionItems = [
            {
                key: 'details',
                label: 'Szczegoly',
                icon: StatusUp,
                onClick: () => dispatch(setSelectedUser(user)),
            },
            {
                key: 'message',
                label: 'Wyslij wiadomosc',
                icon: PersonalCard,
                onClick: () => dispatch(openMessageModal(user)),
            },
            {
                key: 'statistics',
                label: 'Statystyki',
                icon: Chart,
                onClick: () => dispatch(setSelectedUser(user)),
            },
            {
                key: 'edit',
                label: 'Edytuj',
                icon: Edit,
                onClick: () => {
                    dispatch(setEditedUser(user));
                    dispatch(toggleEditModal());
                },
            },
            {
                key: 'block',
                label: 'Zablokuj',
                icon: Lock,
                danger: true,
                disabled: true,
            },
        ];

        if (pageTab === 'messageReports') {
            const resolve = async () => {
                if (user.reportGroupId) {
                    await ApiService.resolveMessageReportGroup(user.reportGroupId);
                } else if (user.reportIds?.length) {
                    await ApiService.resolveMessageReports(user.reportIds);
                }
                window.location.reload();
            };
            const reportActionItems = [
                {
                    key: 'details',
                    label: 'Szczegóły',
                    icon: StatusUp,
                    onClick: () => onMessageReportDetails?.(user),
                },
                {
                    key: 'resolve',
                    label: 'Oznacz jako rozwiązane',
                    icon: StatusUp,
                    onClick: resolve,
                },
            ];
            return [
                <label key={`checkbox-${user.id}`} className="checkbox-wrapper"><input type="checkbox" checked={isChecked} onChange={() => dispatch(toggleCheckedItem(user.id))} /></label>,
                user.reportGroupId || user.id,
                user.senderName,
                user.bunch?.name || user.bunchId,
                user.reportCount,
                user.messageCount,
                user.reportReason,
                user.status,
                <ActionMenu key={`actions-${user.id}`} items={reportActionItems} />,
            ];
        }

        if (pageTab === 'reports') {
            const reportActionItems = [
                {
                    key: 'details',
                    label: 'Szczegoly',
                    icon: StatusUp,
                    onClick: () => {
                        if (typeof onReportDetails === 'function') {
                            onReportDetails(user);
                            return;
                        }
                        dispatch(setSelectedUser(user));
                    },
                },
                {
                    key: 'warning',
                    label: 'Ostrzezenie',
                    icon: Danger,
                    onClick: () => {
                        runReportModerationAction(user, 'Ostrzeżenie', ApiService.putWarning);
                    },
                },
                {
                    key: 'ban',
                    label: 'Blokada',
                    icon: Lock,
                    danger: true,
                    onClick: () => {
                        runReportModerationAction(user, 'Blokada', ApiService.putBan);
                    },
                },
            ];

            return [
                <label key={`checkbox-${user.id}`} className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => dispatch(toggleCheckedItem(user.id))}
                    />
                </label>,
                user.id,
                <span className="user-name-cell" key={`name-${user.id}`}>
                    <strong>{user.nickname}</strong> <span className="user-fullname">({user.fullName})</span>
                </span>,
                user.gender,
                <span className="email-cell">{user.email}</span>,
                user.phone,
                user.reportDate,
                user.reporterName,
                user.reportReason,
                <ActionMenu key={`actions-${user.id}`} items={reportActionItems} />,
            ];
        }

        return [
            <label key={`checkbox-${user.id}`} className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => dispatch(toggleCheckedItem(user.id))}
                />
            </label>,
            user.id,
            <span className="user-name-cell" key={`name-${user.id}`}>
                <strong>{user.nickname}</strong> <span className="user-fullname">({user.fullName})</span>
            </span>,
            user.gender,
            <span className="email-cell">{user.email}</span>,
            user.phone,
            user.registrationDate,
            <div className="status-cell">
                <StatusBadge status={statusType} label={statusLabel} />
            </div>,
            user.createdPacks,
            user.likedEvents,
            <ActionMenu key={`actions-${user.id}`} items={defaultActionItems} />,
        ];
    });
};
